/** @format */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
// import _ from 'lodash';
import axios from 'src/utils/axios';
import { ACTION_CONSTANTS, ENDPOINTS } from 'src/constants';
import type { AppThunk } from 'src/store';
import type { NewsletterConfig, User, UserInfoResponse, UserProfileState, WidgetDashboardResponse } from 'src/types';

const USER_PROFILE_API_URL = process.env.REACT_APP_USER_PROFILE_API_URL;

export interface UsersState {
  userLoading: boolean;
  dashboardLoading: boolean;
  dashboardInfo: WidgetDashboardResponse | null;
  /** Legacy typo kept for runtime compatibility with setDashboardInfoSuccess */
  dashboradInfo: WidgetDashboardResponse | null;
  currentUser: User | null;
  widgetInfo: WidgetDashboardResponse | null;
  error: { error?: string; message?: string } | null;
  onBoardingComplete: boolean;
  newsletterConfig: NewsletterConfig | null;
  newsletterConfigLoading: boolean;
  dashboardError: boolean | null;
  userProfile: UserProfileState;
  currentUserDashboard: string | null;
  userData: User | Record<string, unknown> | null;
}

const initialState: UsersState = {
  userLoading: false,
  dashboardLoading: false,
  dashboardInfo: null,
  dashboradInfo: null,
  currentUser: null,
  widgetInfo: null,
  error: null,
  onBoardingComplete: false,
  newsletterConfig: null,
  newsletterConfigLoading: false,
  dashboardError: null,
  userProfile: {
    picture: null,
    pictureLoading: false,
    error: null
  },
  currentUserDashboard: null,
  userData: null
};

const slice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    //Get User

    getUser(state) {
      state.userLoading = true;
    },
    getUserSuccess(state, action: PayloadAction<{ user?: User } | Record<string, unknown>>) {
      const { user } = action.payload as { user?: User };
      state.currentUser = user ?? null;
      state.userLoading = false;
    },
    getUserFailure(state, action: PayloadAction<{ error?: unknown } | unknown>) {
      const payload = action.payload as { error?: unknown };
      state.error = payload?.error ?? action.payload;
      state.userLoading = false;
    },

    //Get  widget info
    getWidgetInfo(state) {
      state.userLoading = true;
    },
    getWidgetInfoSuccess(state, action: PayloadAction<{ data?: WidgetDashboardResponse | null }>) {
      state.widgetInfo = action.payload?.data ?? null;
      state.userLoading = false;
    },
    getWidgetInfoFailure(state, action: PayloadAction<unknown>) {
      // const { error } = action.payload;
      state.error = action.payload;
      state.userLoading = false;
    },
    // Dashboard Info
    setDashboardInfo(state) {
      state.userLoading = true;
    },
    setDashboardInfoSuccess(state, action: PayloadAction<{ data?: WidgetDashboardResponse | null }>) {
      state.dashboradInfo = action.payload?.data ?? null;
      state.userLoading = false;
    },
    setDashboardInfoFailure(state, action: PayloadAction<unknown>) {
      state.error = action.payload;
      state.userLoading = false;
    },
    // Update Users
    updateUser(state) {
      state.userLoading = true;
    },
    updateUserSuccess(state, action: PayloadAction<{ user?: User }>) {
      if (action.payload.user) state.currentUser = action.payload.user;
      state.userLoading = false;
    },
    updateUserFailure(state, action: PayloadAction<{ error?: unknown } | unknown>) {
      const payload = action.payload as { error?: unknown };
      state.error = payload?.error ?? action.payload;
      state.userLoading = false;
    },
    // UserProfile
    getUserProfilePicture(state) {
      state.userProfile.pictureLoading = true;
    },
    setUserProfilePicture(state, action: PayloadAction<{ picture?: string | null }>) {
      const { picture } = action.payload;
      state.userProfile.pictureLoading = false;
      state.userProfile.error = null;
      state.userLoading = false;
      state.error = null;
      if (picture) {
        state.userProfile.picture = picture;
      }
    },
    setUserProfilePictureFailure(state) {
      state.userProfile.pictureLoading = false;
      state.userProfile.picture = null;
      state.userProfile.error = true;
      state.userLoading = false;
    },
    updateUserProfilePictureSuccess(state) {
      state.userLoading = false;
      state.error = null;
      // Allow a fresh GET after upload.
      state.userProfile.error = null;
    },
    deleteUserProfilePictureSuccess(state) {
      state.userLoading = false;
      state.error = null;
      state.userProfile.picture = null;
      // No picture expected — skip GET until a new upload.
      state.userProfile.error = true;
    },
    updateUserProfilePictureFailure(state, action: PayloadAction<{ error?: unknown } | unknown>) {
      state.error = action.payload;
      state.userLoading = false;
    },
    setUserData(state, action: PayloadAction<User | Record<string, unknown>>) {
      state.userData = action.payload;
    },
    getNewsletterConfig(state) {
      state.newsletterConfigLoading = true;
    },
    getNewsletterConfigSuccess(state, action: PayloadAction<NewsletterConfig | null>) {
      state.newsletterConfig = action.payload;
      state.newsletterConfigLoading = false;
    },
    getNewsletterConfigFailure(state, action: PayloadAction<unknown>) {
      state.newsletterConfigLoading = false;
      state.error = action.payload;
    },
    getDashboard(state) {
      state.dashboardLoading = true;
      state.dashboardError = null;
      state.dashboardInfo = null;
    },
    getDashboardSuccess(state, action: PayloadAction<{ data?: WidgetDashboardResponse | null }>) {
      state.dashboardLoading = false;
      state.dashboardInfo = action.payload?.data ?? null;
    },
    getDashboardFailure(state) {
      state.dashboardLoading = false;
      state.dashboardError = true;
      state.dashboardInfo = null;
    },
    setCurrentUserDashboard(state, action: PayloadAction<{ dashboardId: string }>) {
      const { dashboardId } = action.payload;
      state.currentUserDashboard = dashboardId;
    },
    setOnBoardingCompleteSuccess(state) {
      state.onBoardingComplete = true;
    }
  }
});

export const reducer = slice.reducer;

export const getUser =
  (): AppThunk =>
  async dispatch => {
    const { getUser, getUserSuccess, getUserFailure } = slice.actions;

    try {
      dispatch(getUser());
      const response = await axios.get<UserInfoResponse>(ENDPOINTS.GET_USER_INFO_ENDPOINT);
      dispatch(getUserSuccess({ user: response.data }));
    } catch (err) {
      dispatch(getUserFailure(err));
    }
  };

export const setUserData =
  (data: unknown): AppThunk =>
  async dispatch => {
    const { setUserData } = slice.actions;
    dispatch(setUserData(data as User | Record<string, unknown>));
  };

export const getWidgetInfo = () => async dispatch => {
  const { getWidgetInfo, getWidgetInfoSuccess, getWidgetInfoFailure } = slice.actions;
  try {
    dispatch(getWidgetInfo());
    const response = await axios.get<WidgetDashboardResponse>(ENDPOINTS.GET_WIDGET_ENDPOINT);
    dispatch(getWidgetInfoSuccess(response));
  } catch (err) {
    dispatch(getWidgetInfoFailure(err));
  }
};

export const setDashboardInfo = (layouts, id) => async dispatch => {
  const { setDashboardInfo, setDashboardInfoSuccess, setDashboardInfoFailure } = slice.actions;
  try {
    dispatch(setDashboardInfo());
    if (id) {
      const response = await axios.patch<WidgetDashboardResponse>(`${ENDPOINTS.SET_DASHBOARD_LAYOUT}/${id}`, layouts);
      dispatch(setDashboardInfoSuccess(response));
    }
  } catch (err) {
    dispatch(setDashboardInfoFailure(err));
  }
};

export const getDashboard =
  ({ dashboardId }) =>
  async dispatch => {
    const { getDashboard, getDashboardSuccess, getDashboardFailure } = slice.actions;
    try {
      dispatch(getDashboard());
      const response = await axios.get<WidgetDashboardResponse>(`${ENDPOINTS.GET_DASHBOARD_ENDPOINT}${dashboardId}`);

      const { data } = response;
      dispatch(getDashboardSuccess({ data }));
    } catch (err) {
      dispatch(getDashboardFailure(err));
    }
  };

export const setCurrentUserDashboard = dashboardId => async dispatch => {
  const { setCurrentUserDashboard } = slice.actions;
  dispatch(setCurrentUserDashboard({ dashboardId }));
};

export const updateUserInfo = payload => async dispatch => {
  const { updateUser, updateUserSuccess, updateUserFailure, setUserData } = slice.actions;

  try {
    dispatch(updateUser());
    const response = await axios.put<UserInfoResponse | { user: UserInfoResponse }>(
      ENDPOINTS.GET_USER_INFO_ENDPOINT,
      payload
    );
    const data = response.data;
    const user = data && typeof data === 'object' && 'user' in data ? data.user : data;
    dispatch(
      updateUserSuccess({
        user: user || payload
      })
    );
    dispatch(setUserData(payload));
    return response;
  } catch (err) {
    dispatch(
      updateUserFailure({
        error: err?.message || 'Failed to update user.'
      })
    );
  }
};

export const updateNewsletterConfig =
  ({ uuid, payload }) =>
  async dispatch => {
    const { updateUser, updateUserProfilePictureFailure, updateUserSuccess } = slice.actions;

    try {
      dispatch(updateUser());
      const response = await axios.put(`${ENDPOINTS.ONBOARDING_COMPLETE_ENDPOINT}/${uuid}/newsletter/config`, payload);
      dispatch(updateUserSuccess({ user: response?.data?.user || response?.data || payload }));
      return response;
    } catch (err) {
      dispatch(updateUserProfilePictureFailure({ error: err.message }));
    }
  };

export const getNewsletterConfig = uuid => async dispatch => {
  const { getNewsletterConfig, getNewsletterConfigSuccess, getNewsletterConfigFailure } = slice.actions;

  try {
    dispatch(getNewsletterConfig());
    const response = await axios.get<NewsletterConfig>(
      `${ENDPOINTS.ONBOARDING_COMPLETE_ENDPOINT}/${uuid}/newsletter/config`
    );
    dispatch(getNewsletterConfigSuccess(response?.data || null));
    return response;
  } catch (err) {
    dispatch(getNewsletterConfigFailure(err));
  }
};

export const changePassword = payload => async dispatch => {
  const { updateUser, updateUserSuccess, updateUserFailure } = slice.actions;
  const { uuid } = payload;
  try {
    dispatch(updateUser());
    const response = await axios.put(`${ENDPOINTS.GET_USER_ENDPOINT}/password`, payload);
    return response;
  } catch (err) {
    dispatch(updateUserFailure(err));
  }
};

/** Dedupes StrictMode double-mount and concurrent callers. */
let profilePictureRequest = null;

// User Profile Picture
export const updateUserProfilePicture = () => async (dispatch, getState) => {
  const { getUserProfilePicture, setUserProfilePicture, setUserProfilePictureFailure } = slice.actions;
  const { userProfile } = getState().users;

  if (userProfile.picture || userProfile.error) {
    return;
  }

  if (profilePictureRequest) {
    return profilePictureRequest;
  }

  profilePictureRequest = (async () => {
    try {
      dispatch(getUserProfilePicture());
      // Treat 404 as an expected "no picture" response (not a thrown error).
      const response = await axios.get(`${USER_PROFILE_API_URL}api/v1/user/picture`, {
        responseType: 'arraybuffer',
        validateStatus: status => status === 200 || status === 404
      });

      if (response.status === 404 || !response.data || response.data.byteLength === 0) {
        dispatch(setUserProfilePictureFailure());
        return;
      }

      const contentType = response.headers?.['content-type'] || 'image/png';
      const picture = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      dispatch(setUserProfilePicture({ picture }));
    } catch (err) {
      dispatch(setUserProfilePictureFailure());
    } finally {
      profilePictureRequest = null;
    }
  })();

  return profilePictureRequest;
};

export const uploadUserProfilePicture = payload => async dispatch => {
  const { updateUser, updateUserProfilePictureFailure, updateUserProfilePictureSuccess } = slice.actions;
  const { data } = payload;
  try {
    dispatch(updateUser());
    const response = await axios.post(`${USER_PROFILE_API_URL}api/v1/user/picture`, data, {
      headers: {
        'Content-Type': 'image/png'
      }
    });
    if (!(response as { error?: unknown }).error) {
      dispatch(updateUserProfilePictureSuccess());
    }
  } catch (err) {
    dispatch(updateUserProfilePictureFailure({ error: err.message }));
  }
};

export const deleteUserProfilePicture = (_payload?: unknown) => async dispatch => {
  const { updateUser, deleteUserProfilePictureSuccess } = slice.actions;
  try {
    dispatch(updateUser());
    await axios.delete(`${USER_PROFILE_API_URL}api/v1/user/picture`);
    dispatch(deleteUserProfilePictureSuccess());
  } catch (err) {
    // Missing picture on delete is non-fatal.
  }
};

export const onBoardingComplete = createAsyncThunk(
  ACTION_CONSTANTS.ON_BOARDING_COMPLETE,
  async (payload: { uuid: string }) => {
    const { uuid } = payload;
    const response = await axios.post(`${ENDPOINTS.ONBOARDING_COMPLETE_ENDPOINT}/${uuid}/onboarding-complete`);
    return response.status;
  }
);

export const completeOnBoarding = (uuid: string) => async dispatch => {
  try {
    await dispatch(onBoardingComplete({ uuid })).unwrap();
    return true;
  } catch (err) {
    return false;
  }
};

export default slice;
